import { DepositPaymentEvent, OrderState, PaymentEventEnum, useApiTradeV1TradeCompaniesPaymentTermsList } from 'api';
import {
  BooleanSelectField,
  CompanyPaymentTermsAutocomplete,
  createPaymentTermLabel,
  PaymentTermOption,
  PriceAndCurrencyField,
  SelectField,
  type SelectOption,
  SwitchField,
  TextField,
  useFormContext,
  useResetOnChange,
  useWatch,
} from 'form';
import { type I18nTranslateFunction, useTranslation } from 'i18n';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { Box, SectionContainer } from 'ui';

import { PurchaseOrderFormContext } from '../../contexts/PurchaseOrderFormContext';
import { CreateFormValues } from '../../pages/PurchaseOrderCreate/PurchaseOrderCreate';
import { VariablePricingFields } from '..';
import { PaymentMethod } from '../PaymentMethod/PaymentMethod';

const getDepositPaymentEventOptions = (t: I18nTranslateFunction<string>): SelectOption[] => [
  {
    label: t('financialDetailsSection.depositPaymentEvents.uponReceiptOfSignedPo'),
    value: DepositPaymentEvent.SIGNED_PO,
  },
  {
    label: t('financialDetailsSection.depositPaymentEvents.uponReceiptOfSignedPoAndSuccessfulPreInspection'),
    value: DepositPaymentEvent.SIGNED_PO_AND_PREINSPECTION,
  },
  {
    label: t('financialDetailsSection.depositPaymentEvents.collectionFromSupplier'),
    value: DepositPaymentEvent.COLLECTION,
  },
  {
    label: t('financialDetailsSection.depositPaymentEvents.afterSuccessfulPreInspectionAndCollectionFromSupplier'),
    value: DepositPaymentEvent.PREINSPECTION_AND_COLLECTION,
  },
];

const getPaymentEventOptions = (t: I18nTranslateFunction<string>): SelectOption[] => [
  {
    label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
    value: PaymentEventEnum.CL,
  },
  {
    label: t('buyerFinancialDetails.paymentEvents.export'),
    value: PaymentEventEnum.XP,
  },
  {
    label: t('buyerFinancialDetails.paymentEvents.billOfLading'),
    value: PaymentEventEnum.BL,
  },
  {
    label: t('buyerFinancialDetails.paymentEvents.destinationPortArrival'),
    value: PaymentEventEnum.DA,
  },
  {
    label: t('buyerFinancialDetails.paymentEvents.icdArrival'),
    value: PaymentEventEnum.IA,
  },
  {
    label: t('buyerFinancialDetails.paymentEvents.siteDelivery'),
    value: PaymentEventEnum.SD,
  },
];

type Props = {
  isBuyer: boolean;
  currencyPriceName: string;
};

export const FinancialDetailsSection = ({ currencyPriceName }: Props) => {
  const { t } = useTranslation('orders');
  const { setValue, resetField } = useFormContext<CreateFormValues>();
  const { variant, defaultValues, purchaseOrderState } = useContext(PurchaseOrderFormContext);
  const {
    incoterm,
    is_variable_pricing: isVariablePricing,
    seller_id: sellerId,
    company_payment_term_id: companyPaymentTermId,
  } = useWatch<CreateFormValues>();
  const [selectedPaymentTerm, setSelectedPaymentTerm] = useState<PaymentTermOption | undefined | null>();
  const isCustomPaymentTerm = companyPaymentTermId === undefined;
  const depositPaymentEventOptions = useMemo(() => getDepositPaymentEventOptions(t), [t]);
  const paymentEventOptions = useMemo(() => getPaymentEventOptions(t), [t]);

  const { data: paymentTermsData } = useApiTradeV1TradeCompaniesPaymentTermsList(sellerId || 0, undefined, {
    query: {
      enabled: Boolean(sellerId),
    },
  });

  const getSelectedPaymentTerm = useCallback(() => {
    if (!paymentTermsData?.results || !sellerId) {
      return null;
    }

    // If we have a company_payment_term_id, find the matching term
    if (companyPaymentTermId) {
      const matchingTerm = paymentTermsData.results.find((term) => term.id === companyPaymentTermId);
      if (matchingTerm) {
        return {
          isCustom: false,
          label: createPaymentTermLabel(matchingTerm),
          term: matchingTerm,
          value: matchingTerm.id.toString(),
        };
      }
    }

    // If no company_payment_term_id, it's a custom payment term
    return {
      isCustom: true,
      label: t('financialDetailsSection.paymentTerms.custom'),
      term: null,
      value: 'custom',
    };
  }, [sellerId, paymentTermsData?.results, companyPaymentTermId, t]);

  const handlePaymentTermSelect = useCallback(
    (option: PaymentTermOption | null) => {
      setSelectedPaymentTerm(option);
      if (option?.term) {
        setValue('company_payment_term_id', option.term.id);
        setValue('deposit_percentage', option.term.deposit_percentage);
        if (option.term.deposit_payment_event) {
          setValue('deposit_payment_event', option.term.deposit_payment_event);
        }
        setValue('deposit_event_offset', Math.abs(option.term.deposit_event_offset_days || 0));
        setValue(
          'deposit_event_offset_direction',
          option.term.deposit_event_offset_days ? (option.term.deposit_event_offset_days >= 0 ? 1 : -1) : 1
        );
        setValue('payment_event', option.term.payment_event);
        setValue('due_date_offset', Math.abs(option.term.due_date_offset_days));
        setValue('due_date_direction', option.term.due_date_offset_days >= 0 ? 1 : -1);
        setValue('payment_terms_comments', option.term.default_payment_comment);
      } else {
        setValue('company_payment_term_id', undefined);
        setValue('deposit_percentage', '0');
        setValue('deposit_payment_event', undefined);
        setValue('deposit_event_offset', 0);
        setValue('deposit_event_offset_direction', 1);
        setValue('payment_terms_comments', '');
        setValue('payment_event', '' as PaymentEventEnum);
      }
    },
    [setValue]
  );

  useEffect(() => {
    if (companyPaymentTermId && variant === 'update') {
      const paymentTerm = getSelectedPaymentTerm();

      handlePaymentTermSelect(paymentTerm);
    }
    // This is fine to do as we only want this to run when the paymentTermsData changes
    // so we can set the payment term that was initially set on the SO
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paymentTermsData]);

  useEffect(() => {
    const preferred = paymentTermsData?.results.find((term) => term.is_preferred);

    if (preferred && paymentTermsData && variant === 'create') {
      handlePaymentTermSelect({
        isCustom: false,
        label: createPaymentTermLabel(preferred),
        term: preferred,
        value: preferred.id.toString(),
      });
    }
  }, [paymentTermsData, handlePaymentTermSelect, variant]);

  useResetOnChange<CreateFormValues>('payment_event', ['incoterm'], resetField);

  const paymentTermsFieldDisabled = variant === 'update' && purchaseOrderState === OrderState.PE;
  const paymentFieldsDisabled = !!sellerId && !isCustomPaymentTerm;

  return (
    <SectionContainer title={t('financialDetailsSection.title')}>
      <SwitchField name="is_variable_pricing" label={t('financialDetailsSection.labels.variablePricing')} />
      <PriceAndCurrencyField
        data-testid="price-per-tonne"
        slotProps={{
          currency: {
            inputProps: {
              'aria-label': t('financialDetailsSection.labels.sellerCurrency'),
            },
            name: currencyPriceName,
          },
          price: {
            infoTooltip: t('financialDetailsSection.tooltips.pricePerTonne'),
            label: t('financialDetailsSection.labels.pricePerTonne'),
            name: 'price_per_tonne',
            required: true,
          },
        }}
      />
      <PriceAndCurrencyField
        data-testid="third-party-commission"
        slotProps={{
          currency: {
            inputProps: {
              'aria-label': t('financialDetailsSection.labels.thirdPartyCommissionCurrency'),
            },
            name: 'third_party_commission_currency',
          },
          price: {
            infoTooltip: t('financialDetailsSection.tooltips.commission'),
            label: t('financialDetailsSection.labels.commission'),
            name: 'third_party_commission_per_tonne',
          },
        }}
      />
      <PaymentMethod
        sx={{ gridColumn: 'span 3' }}
        required={true}
        infoTooltip={t('financialDetailsSection.tooltips.paymentMethod')}
      />
      <BooleanSelectField
        name="payment_before_material_is_released"
        label={t('financialDetailsSection.labels.paymentBeforeMaterialIsReleased')}
        required
        infoTooltip={t('financialDetailsSection.tooltips.paymentBeforeMaterialIsReleased')}
      />
      <CompanyPaymentTermsAutocomplete
        value={selectedPaymentTerm}
        name="payment_term_id"
        label={t('financialDetailsSection.labels.paymentTerms')}
        tradeCompanyId={sellerId}
        onSelectOption={handlePaymentTermSelect}
        infoTooltip={t('financialDetailsSection.tooltips.paymentTerms')}
        disabled={paymentTermsFieldDisabled}
      />
      <TextField
        type="number"
        label={t('financialDetailsSection.labels.depositPercentageUpfront')}
        name="deposit_percentage"
        infoTooltip={t('financialDetailsSection.tooltips.depositPercentageUpfront')}
        disabled={paymentFieldsDisabled}
      />
      <SelectField
        label={t('financialDetailsSection.labels.depositPaymentEvent')}
        name="deposit_payment_event"
        options={depositPaymentEventOptions}
        infoTooltip={t('financialDetailsSection.tooltips.depositPaymentEvent')}
        disabled={paymentFieldsDisabled}
      />
      <Box
        sx={{
          flexDirection: 'row',
          display: 'flex',
        }}
      >
        <TextField
          type="number"
          label={t('financialDetailsSection.labels.depositEventOffset')}
          name="deposit_event_offset"
          sx={{ flexShrink: 1 }}
          disabled={paymentFieldsDisabled}
        />
        <SelectField
          label=""
          data-testid="deposit_event_offset_direction"
          name="deposit_event_offset_direction"
          fullWidth={true}
          infoTooltip={t('financialDetailsSection.tooltips.daysBeforeOrAfterPaymentEvent')}
          options={[
            {
              label: t('buyerFinancialDetails.dueDateDirection.before'),
              value: '-1',
            },
            {
              label: t('buyerFinancialDetails.dueDateDirection.after'),
              value: '1',
            },
          ]}
          disabled={paymentFieldsDisabled}
        />
      </Box>
      <TextField
        minRows={2}
        label={t('financialDetailsSection.labels.paymentTermsComments')}
        name="payment_terms_comments"
        infoTooltip={t('financialDetailsSection.tooltips.daysBeforeOrAfterDepositPaymentEvent')}
        disabled={paymentFieldsDisabled}
      />
      <Box
        sx={{
          flexDirection: 'row',
          display: 'flex',
        }}
      >
        <TextField
          type="number"
          label={t('financialDetailsSection.labels.dueDateOffset')}
          name="due_date_offset"
          sx={{ flexShrink: 1 }}
          disabled={paymentFieldsDisabled}
        />
        <SelectField
          label=""
          data-testid="due_date_direction"
          name="due_date_direction"
          fullWidth={true}
          infoTooltip={t('financialDetailsSection.tooltips.daysBeforeOrAfterPaymentEvent')}
          options={[
            {
              label: t('buyerFinancialDetails.dueDateDirection.before'),
              value: '-1',
            },
            {
              label: t('buyerFinancialDetails.dueDateDirection.after'),
              value: '1',
            },
          ]}
          disabled={paymentFieldsDisabled}
        />
      </Box>
      <SelectField
        label={t('financialDetailsSection.labels.paymentEvent')}
        name="payment_event"
        options={incoterm ? getBuyerPaymentEventOptions(incoterm, t) : paymentEventOptions}
        infoTooltip={t('financialDetailsSection.tooltips.paymentEvent')}
        disabled={paymentFieldsDisabled}
      />
      {isVariablePricing ? <VariablePricingFields defaultValues={defaultValues} formVariant={variant} /> : null}
    </SectionContainer>
  );
};

const getBuyerPaymentEventOptions = (
  incoterm: CreateFormValues['incoterm'] | undefined,
  t: I18nTranslateFunction<string>
) => {
  switch (incoterm) {
    case 'EXW':
    case 'FCA':
    case 'FAS':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
          value: PaymentEventEnum.CL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.export'),
          value: PaymentEventEnum.XP,
        },
      ];
    case 'FOB':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
          value: PaymentEventEnum.CL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.export'),
          value: PaymentEventEnum.XP,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.billOfLading'),
          value: PaymentEventEnum.BL,
        },
      ];
    case 'CFR':
    case 'CIF':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
          value: PaymentEventEnum.CL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.export'),
          value: PaymentEventEnum.XP,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.billOfLading'),
          value: PaymentEventEnum.BL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.destinationPortArrival'),
          value: PaymentEventEnum.DA,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.icdArrival'),
          value: PaymentEventEnum.IA,
        },
      ];
    case 'DDP':
    case 'DAP':
      return [
        {
          label: t('buyerFinancialDetails.paymentEvents.collectionFromSupplier'),
          value: PaymentEventEnum.CL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.export'),
          value: PaymentEventEnum.XP,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.billOfLading'),
          value: PaymentEventEnum.BL,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.destinationPortArrival'),
          value: PaymentEventEnum.DA,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.icdArrival'),
          value: PaymentEventEnum.IA,
        },
        {
          label: t('buyerFinancialDetails.paymentEvents.siteDelivery'),
          value: PaymentEventEnum.SD,
        },
      ];
    default:
      return [];
  }
};
